/* eslint-disable @typescript-eslint/no-explicit-any */
import { defineStore, storeToRefs } from 'pinia'
import {
  EscoLanguage,
  type EscoOccupationDto,
  type JobMatchOccupation,
  type OccupationUpdateRequest
} from '../../../../generated-api'
import { factory } from '../../../../factory-api/factory'
import { useUserStore, useJobmatchStore, useLanguageStore } from '../..'
import type { IStackedCard } from '../../../interfaces/components/IStackCard'
import { capitalizeFirstLetter, getCountryMapping } from '../../../application/helpers'
import type { SelectItem } from '@/src/interfaces/components/ISelectComponent'

export const useOccupationStore = defineStore('occupation', () => {
  const jobmatchStore = useJobmatchStore()
  const langaugeStore = useLanguageStore()

  const { currentCountry } = storeToRefs(langaugeStore)

  const editingOccupation = ref<boolean>(false)
  const selectedOccupations = ref<JobMatchOccupation[]>([])
  const userId = useUserStore().member.id
  const foundOccupations = ref<EscoOccupationDto[]>([])
  const occupations = ref<SelectItem[] | []>([])
  const loading = ref<boolean>(false)

  const addOccupation = async (newOccupation: JobMatchOccupation) => {
    const doesExist = selectedOccupations.value.find(
      (so: JobMatchOccupation) => so.label === newOccupation.label
    )
    if (doesExist === undefined) {
      selectedOccupations.value.push(newOccupation)
      editingOccupation.value = true
      try {
        const req: OccupationUpdateRequest = {
          candidateId: userId,
          occupations: selectedOccupations.value
        }
        const jobMatchApi = await factory.getJobMatchApi()
        await jobMatchApi.addJobMatchProfileOccupations({ occupationUpdateRequest: req })
        await jobmatchStore.getJobMatchCount()
      } catch (err) {
        console.warn('Error on addOccupation: ', err)
      } finally {
        editingOccupation.value = false
      }
    }
  }

  const removeOccupation = async (occupation: JobMatchOccupation) => {
    editingOccupation.value = true
    // Remove Occupation from selected occupations list
    const index = selectedOccupations.value.findIndex((selectedOccupation: JobMatchOccupation) => {
      if (selectedOccupation.label?.toLowerCase() === occupation.label?.toLowerCase()) {
        return true
      } else {
        return false
      }
    })
    // Remove it from API
    try {
      const req: OccupationUpdateRequest = {
        candidateId: userId,
        occupations: [occupation]
      }
      const jobMatchApi = await factory.getJobMatchApi()
      await jobMatchApi.removeJobMatchProfileOccupations({ occupationUpdateRequest: req })
      selectedOccupations.value.splice(index, 1)
      await jobmatchStore.getJobMatchCount()
    } catch (err) {
      console.warn('Error on addOccupation: ', err)
    } finally {
      editingOccupation.value = false
    }
  }

  const setSelectedOccupation = (occupations: JobMatchOccupation[] | undefined) => {
    selectedOccupations.value = occupations !== undefined ? occupations : []
  }

  const handleSelection = async (
    card: IStackedCard,
    occupationCode: string | undefined,
    occupationSource: string
  ): Promise<IStackedCard> => {
    const occupation: JobMatchOccupation = {
      label: card.name,
      occupationSource: occupationSource,
      occupationCode: occupationCode
    }

    await addOccupation(occupation)
    return card
  }

  const handleRemoval = async (
    card: IStackedCard,
    selectedValues: IStackedCard[],
    occupationSource: string
  ): Promise<IStackedCard[]> => {
    const occupation: JobMatchOccupation = {
      label: card.name,
      occupationSource: occupationSource
    }

    await removeOccupation(occupation)
    return selectedValues.filter((sCard: IStackedCard) => card.name !== sCard.name)
  }

  const setOccupationsFromJobDescription = async (
    jobDescription: string,
    noOfResults: number = 50
  ) => {
    const occupations = await occupationsFromJobDescription(jobDescription, noOfResults)
    if (occupations !== undefined) {
      foundOccupations.value = occupations
    }
  }

  const occupationsFromJobDescription = async (
    jobDescription: string,
    noOfResults: number = 50
  ) => {
    try {
      if (currentCountry.value != undefined) {
        const countryMapping = getCountryMapping(currentCountry.value.country)

        const capitalizedShortName = capitalizeFirstLetter(countryMapping.language.shortName)
        const jobMatchApi = await factory.getJobMatchApi()
        const response = await jobMatchApi.jobDescriptionToOccupations({
          jobDescriptionToOccupationsRequest: {
            jobDescription: jobDescription,
            returnLanguage: capitalizedShortName as EscoLanguage,
            noOfResults
          }
        })

        if (response.occupations !== undefined) {
          return response.occupations
        }
      }
    } catch (error) {
      console.warn('Error in jobDescriptionToOccupation: ', error)
    }
    return undefined
  }

  const setFoundOccupations = (occupations: EscoOccupationDto[]) => {
    foundOccupations.value = occupations
  }

  const getOccupations = async (name: string) => {
    try {
      let currentLanguage = langaugeStore.currentCountry?.language

      if (!currentLanguage && currentCountry.value) {
        currentLanguage = getCountryMapping(currentCountry.value.country).language
      }

      if (currentCountry.value != undefined) {
        const jobMatchApi = await factory.getJobMatchApi()
        const response = await jobMatchApi.getOccupationsByString({
          occupationByTextRequest: {
            text: name.toLowerCase(),
            language: currentLanguage?.shortName as EscoLanguage
          }
        })

        occupations.value = response.map((item) => ({
          id: item.code,
          name: capitalizeFirstLetter(item.label ?? '')
        }))
      } else {
        occupations.value = []
      }
    } catch (error) {
      console.warn('Error in getOccupations', error)
      occupations.value = []
    }
  }

  return {
    editingOccupation,
    selectedOccupations,
    occupations,
    loading,
    addOccupation,
    getOccupations,
    removeOccupation,
    setSelectedOccupation,
    handleSelection,
    handleRemoval,
    setOccupationsFromJobDescription,
    occupationsFromJobDescription,
    foundOccupations,
    setFoundOccupations
  }
})
